import { quotationRows, profileBom, glassBom, infillBom, hardwareOrder, cutPlan, barsByProfile } from "@/lib/bom";

export const RH = 4.6, CAP = 244, GAP = 10, BAR_ROW = 7, HEAD_ROW = 8;
const m2 = (v) => v.toFixed(2);

function flow(sections) {
  const pages = [[]];
  let y = 0;
  sections.filter((s) => s.rows.length).forEach((sec) => {
    let rows = sec.rows;
    while (rows.length) {
      const room = Math.floor((CAP - y - GAP - RH) / RH);
      if (room < 3) { pages.push([]); y = 0; continue; }
      const take = rows.slice(0, room);
      pages[pages.length - 1].push({ ...sec, rows: take, y, cont: rows !== sec.rows });
      y += GAP + RH * (take.length + 1);
      rows = rows.slice(room);
    }
  });
  return pages.filter((p) => p.length);
}

function cutFlow(plan) {
  const pages = [[]];
  let y = 0;
  plan.groups.forEach((g) => {
    const lines = [{ t: "head", g }, ...g.patterns.map((p, i) => ({ t: "bar", g, p, i })), ...g.oversize.map((c) => ({ t: "over", g, c }))];
    lines.forEach((l, k) => {
      const h = l.t === "head" ? HEAD_ROW : BAR_ROW;
      if (y + h + (k === 0 ? BAR_ROW : 0) > CAP) { pages.push([]); y = 0; }
      pages[pages.length - 1].push({ ...l, y });
      y += h;
    });
    y += 2;
  });
  return pages.filter((p) => p.length);
}

export function bomPages(items, s) {
  const plan = cutPlan(items, s), bars = barsByProfile(plan), q = quotationRows(items);
  const qty = q.reduce((a, r) => a + r.qty, 0), area = q.reduce((a, r) => a + r.total, 0);
  const quote = {
    title: "QUOTATION SCHEDULE (QUANTITIES)",
    cols: [{ h: "SR", w: 8 }, { h: "TAG", w: 14 }, { h: "DESCRIPTION", w: 44 }, { h: "TYPOLOGY", w: 32 }, { h: "SYSTEM", w: 64 }, { h: "W × H mm", w: 26 }, { h: "m²/UNIT", w: 16 }, { h: "QTY", w: 12 }, { h: "TOTAL m²", w: 18 }, { h: "RATE", w: 22 }, { h: "AMOUNT", w: 28 }],
    rows: [...q.map((r) => [r.sr, r.tag, r.desc, r.typology, r.system, `${r.w} × ${r.h}`, m2(r.area), r.qty, m2(r.total), "", ""]),
      ["", "", "TOTAL", "", "", "", "", qty, m2(area), "SUBTOTAL", ""], ["", "", "", "", "", "", "", "", "", `GST ${s.gst}%`, ""], ["", "", "", "", "", "", "", "", "", "GRAND TOTAL", ""]],
  };
  const prof = {
    title: "PROFILE BILL OF MATERIALS",
    cols: [{ h: "PROFILE / MEMBER", w: 44 }, { h: "CODE", w: 22 }, { h: "SYSTEM", w: 64 }, { h: "PIECES", w: 16 }, { h: "LENGTH m", w: 20 }, { h: `BARS ${s.bar}`, w: 20 }, { h: "USED IN", w: 98 }],
    rows: profileBom(items).map((p) => [p.name, p.code || "—", p.system, p.count, p.m.toFixed(2), bars.get(p.key) || 0, p.tags]),
  };
  const glass = {
    title: "GLASS BILL OF MATERIALS (SIZES INCL. BITE)",
    cols: [{ h: "GLASS SPECIFICATION", w: 120 }, { h: "PANES", w: 20 }, { h: "AREA m²", w: 24 }, { h: "USED IN", w: 120 }],
    rows: glassBom(items).map((g) => [g.spec, g.panes, m2(g.area), g.tags]),
  };
  const inf = infillBom(items);
  const infill = {
    title: "INFILL PANELS — NON-GLASS (SIZES INCL. BITE)",
    cols: [{ h: "MATERIAL", w: 34 }, { h: "SPECIFICATION", w: 86 }, { h: "PANELS", w: 20 }, { h: "AREA m²", w: 24 }, { h: "USED IN", w: 120 }],
    rows: [...inf.rows.map((r) => [r.mat.label, r.spec, r.panes, m2(r.area), r.tags]), ...inf.totals.map((t) => [`TOTAL ${t.mat.label.toUpperCase()}`, "", t.panes, m2(t.area), ""])],
  };
  const hw = hardwareOrder(items).map((g) => ({
    title: g.group.toUpperCase(),
    cols: [{ h: "PART", w: 48 }, { h: "SPECIFICATION", w: 78 }, { h: "TOTAL QTY", w: 20 }, { h: "UNIT", w: 12 }, { h: "ITEMS (TAG × QTY)", w: 112 }, { h: "CHECK", w: 14 }],
    rows: g.rows.map((h) => [h.name, h.spec, h.qty, h.unit, h.tags, ""]),
  }));
  return [
    ...flow([quote, prof, glass, infill]).map((sections) => ({ type: "tables", kind: "QUOTATION & BILL OF MATERIALS", suffix: "B", sections })),
    ...flow(hw).map((sections) => ({ type: "tables", kind: "HARDWARE & ACCESSORIES ORDER", suffix: "H", sections })),
    ...cutFlow(plan).map((lines) => ({ type: "cuts", kind: "BAR CUTTING PLAN", suffix: "C", lines, plan })),
  ].map((p) => ({ ...p, settings: s, qty, area, nItems: items.length }));
}
